import { Modal, Box, useTheme } from "@mui/material";
import React, { useContext, useEffect, useRef } from "react";
import { AIVerificationResult } from "../../../api";
import { inspectionsApi } from "../../../api-interface";
import { TenantContext } from "../../..";

// Simple in-memory cache for image blobs and object URLs, with a max size
const MAX_CACHE_SIZE = 20;
const imageCache = new Map<string, { url: string, blob: Blob }>();

// Accepts an array of polygons
const Canvas = (props: React.ComponentProps<'canvas'> & { analysisId: string, pageNumber: number, polygons?: (number[] | { x: number, y: number, width: number, height: number })[] }) => {
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const tenant = useContext(TenantContext)

    useEffect(() => {
        const fetchAndDraw = async () => {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const context = canvas.getContext('2d');
            if (!context) return;
            const cacheKey = `${props.analysisId}:${props.pageNumber}`;
            let url: string | undefined;
            let blob: Blob | undefined;
            if (imageCache.has(cacheKey)) {
                ({ url, blob } = imageCache.get(cacheKey)!);
                imageCache.delete(cacheKey);
                imageCache.set(cacheKey, { url, blob });
            } else {
                try {
                    const response = await inspectionsApi(tenant).getAnalysisImageInspectionsAnalysisIdImageGet(props.analysisId, props.pageNumber, { responseType: 'blob' });
                    blob = response.data as Blob;
                    if (!blob) return;
                    url = URL.createObjectURL(blob);
                    if (imageCache.size >= MAX_CACHE_SIZE) {
                        const oldestKey = imageCache.keys().next().value;
                        const oldest = imageCache.get(oldestKey);
                        if (oldest) {
                            URL.revokeObjectURL(oldest.url);
                        }
                        imageCache.delete(oldestKey);
                    }
                    imageCache.set(cacheKey, { url, blob });
                } catch (e) {
                    return;
                }
            }
            if (!url) return;
            const img = new window.Image();
            img.onload = function () {
                canvas.width = img.width || 600;
                canvas.height = img.height || 800;
                context.clearRect(0, 0, canvas.width, canvas.height);
                context.drawImage(img, 0, 0);
                if (!props.polygons) return;
                const scale = (v: number, max: number) => v > 1 ? v / 100 * max : v * max;
                context.save();
                context.strokeStyle = 'red';
                context.lineWidth = 3;
                for (const pts of props.polygons) {
                    if (Array.isArray(pts) && pts.length === 8) {
                        context.beginPath();
                        context.moveTo(scale(pts[0], img.width), scale(pts[1], img.height));
                        for (let i = 2; i < 8; i += 2) {
                            context.lineTo(scale(pts[i], img.width), scale(pts[i + 1], img.height));
                        }
                        context.closePath();
                        context.stroke();
                    } else if (typeof pts === 'object' && 'x' in pts) {
                        const { x, y, width, height } = pts;
                        context.strokeRect(
                            x * img.width,
                            y * img.height,
                            width * img.width,
                            height * img.height
                        );
                    }
                }
                context.restore();
            };
            img.onerror = function () {};
            img.src = url;
        };
        fetchAndDraw();
    }, [props.analysisId, props.pageNumber, props.polygons, tenant]);

    return <canvas ref={canvasRef} width={600} height={800} {...props} />
}

const DocumentLocationModal = ({ open, handleClose, analysisId, verificationResult }: { open: boolean, handleClose: () => void, analysisId: string, verificationResult: AIVerificationResult }) => {
    const theme = useTheme()
    const tenant = useContext(TenantContext)
    const style = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        bgcolor: 'background.paper',
        minWidth: 100,
        p: 4,
    };

    // Support multiple document locations (polygons)
    const locations = Array.isArray(verificationResult.location) ? verificationResult.location : [];
    const polygons = locations.map(loc => loc.polygon).filter(Boolean);
    // Use the first location's page_number if available, else fallback to 1
    const pageNumber = locations.length && locations[0].page_number ? locations[0].page_number : 1;

    return <Modal
        open={open}
        onClose={handleClose}
    >
        <Box sx={style} display="flex" flexDirection="column" gap={2}>
            <Box>
                <Canvas analysisId={analysisId} pageNumber={pageNumber} style={{ maxWidth: '100%', maxHeight: 600, border: '1px solid #ccc' }} polygons={polygons} />
            </Box>
        </Box>
    </Modal>
}
export default DocumentLocationModal